import React from 'react';

import Tooltip from '@atlaskit/tooltip';

import { ExecutionStatus, TestType } from '@qality/common-model';
import {
  DateFormatService,
  StorageOfUniqueServices,
} from '@qality/common-service';
import { useOverflowObserver } from '@qality/common-utils';

import { ExecutionStatusLozenge } from '../../atoms/ExecutionStatusLozenge';
import { Link } from '../../atoms/Link';
import { Table } from '../../atoms/Table';
import { Text } from '../../atoms/Text';
import { User } from '../../atoms/User';
import { Issue, IssueProps } from '../../molecules/Issue';
import NoPermissionToViewTestCase from '../../molecules/NoPermissionToViewTestCase/NoPermissionToViewTestCase';
import { LinkWrapperStyled } from '../../report/report-columns/ReportColumn.styled';
import UserImpl from '../../select/UserTestImpl';
import {
  NoTestCycleStyled,
  TooltipWrapperStyled,
} from './UnfinishedTestCaseExecutions.styled';
import { ColumnsWidths } from './UnfinishedTestCaseExecutionsTable';

export type UnfinishedTestCaseExecutionsTableRowProps = {
  status: ExecutionStatus;
  createdDate: string;
  assignee: UserImpl;
  testCase: IssueProps;
  testCycle: {
    name: string;
    id: string;
  };
  testTypeProps: {
    testType?: TestType;
    isLoading?: boolean;
    isReadOnly?: boolean;
    isIconOnly?: boolean;
    style?: React.CSSProperties;
  };
  testExecutionUrl?: string;
  key?: string;
};

export default function UnfinishedTestCaseExecutionsTableRow({
  status,
  createdDate,
  assignee,
  testCase,
  testCycle,
  testTypeProps,
  testExecutionUrl,
}: Readonly<UnfinishedTestCaseExecutionsTableRowProps>) {
  const { ref, isOverflowing } = useOverflowObserver();

  return (
    <Table.Tr isClickable isHoverable hideOverflow>
      <Table.Td
        widths={ColumnsWidths.testCase}
        data-testid={`unfinished-test-case-executions-table-row-td-test-case-${testCase?.issueKey}`}
      >
        <Table.ContentHelper alignItems='center' overflow='hidden'>
          {testCase ? (
            <Issue
              addPadding
              issueKey={testCase.issueKey}
              issueLink={StorageOfUniqueServices.get('URLService').getIssueURL(
                testCase.issueKey
              )}
              title={testCase.title}
              testTypeProps={testTypeProps}
              withEllipsis
            />
          ) : (
            <NoPermissionToViewTestCase />
          )}
        </Table.ContentHelper>
      </Table.Td>
      <Table.Td widths={ColumnsWidths.testCycle}>
        <Table.ContentHelper alignItems='center'>
          <TooltipWrapperStyled>
            <Tooltip content={isOverflowing && testCycle.name} testId='tooltip'>
              <LinkWrapperStyled innerRef={ref} maxWidth='100%'>
                {testCycle.name ? (
                  <Link
                    href={StorageOfUniqueServices.get(
                      'URLService'
                    ).getTestCycleUrl(testCycle.id)}
                    gray
                    target='_blank'
                  >
                    {testCycle.name}
                  </Link>
                ) : (
                  <NoTestCycleStyled>None</NoTestCycleStyled>
                )}
              </LinkWrapperStyled>
            </Tooltip>
          </TooltipWrapperStyled>
        </Table.ContentHelper>
      </Table.Td>
      <Table.Td widths={ColumnsWidths.created}>
        <Table.ContentHelper alignItems='center'>
          <Text>
            {DateFormatService.formatDate(createdDate, 'longDateFormat')}
          </Text>
        </Table.ContentHelper>
      </Table.Td>
      <Table.Td widths={ColumnsWidths.executionStatus}>
        <Table.ContentHelper alignItems='center'>
          <ExecutionStatusLozenge status={status} />
        </Table.ContentHelper>
      </Table.Td>
      <Table.Td widths={ColumnsWidths.assignee}>
        <Table.ContentHelper alignItems='center'>
          <User
            userName={assignee.getDisplayName()}
            userAvatar={assignee.getAvatar()}
            isTextVisible={false}
            size='small'
          />
        </Table.ContentHelper>
      </Table.Td>
      <Table.Td widths={ColumnsWidths.action}>
        <Table.ContentHelper alignItems='center'>
          <LinkWrapperStyled>
            <Link
              href={
                StorageOfUniqueServices.get('HostNameService').baseUrl +
                testExecutionUrl
              }
              target='_blank'
            >
              Open execution
            </Link>
          </LinkWrapperStyled>
        </Table.ContentHelper>
      </Table.Td>
    </Table.Tr>
  );
}
